import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, ShieldCheck } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api, API, errMsg } from "../lib/api";

const GOLD = "#C2A462";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export function useBranding() {
  const [b, setB] = useState(null);
  useEffect(() => { fetch(`${API}/public/branding`).then((r) => r.json()).then(setB).catch(() => setB({})); }, []);
  return b;
}

function useFormWidth(img, pct) {
  const [w, setW] = useState(null);
  useEffect(() => {
    const calc = () => {
      const el = img.current;
      if (!el?.naturalWidth) return;
      const rendered = Math.max(window.innerWidth, window.innerHeight * (el.naturalWidth / el.naturalHeight));
      setW(window.innerWidth < 1024 ? window.innerWidth : (rendered * pct) / 100);
    };
    calc();
    window.addEventListener("resize", calc);
    const el = img.current;
    el?.addEventListener("load", calc);
    return () => { window.removeEventListener("resize", calc); el?.removeEventListener("load", calc); };
  }, [img, pct]);
  return w;
}

const Title = ({ children }) => (
  <h1 className="text-center text-[1.9rem] sm:text-[2.2rem] font-medium tracking-wide mt-10 mb-2" style={{ color: GOLD, fontFamily: "'Cormorant Garamond', Georgia, serif" }} data-testid="auth-title">{children}</h1>
);
const Divider = () => <div className="mx-auto mb-7 h-px w-14" style={{ background: GOLD, opacity: 0.6 }} />;

function InImageLayout({ bg, pct, title, children }) {
  const img = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const w = useFormWidth(img, pct);
  const ready = loaded && w;
  return (
    <div className="min-h-screen relative overflow-hidden bg-[#050608]" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="auth-layout">
      <img ref={img} src={bg} alt="" onLoad={() => setLoaded(true)} data-testid="login-background-image"
        className={`absolute inset-0 h-full w-full object-cover object-left transition-opacity duration-700 ${loaded ? "opacity-100" : "opacity-0"}`} />
      <div className="absolute inset-0 bg-[#070d1c]/85 lg:hidden" />
      {ready && (
        <div className="relative z-10 min-h-screen flex items-center justify-center px-10" style={{ width: w }}>
          <div className="w-full max-w-[340px] fade-up"><Title>{title}</Title><Divider />{children}</div>
        </div>
      )}
    </div>
  );
}

export function AuthLayout({ title, children }) {
  const b = useBranding();
  const bg = b?.login_background ? `${BACKEND}${b.login_background}` : null;
  if (b === null) return <div className="min-h-screen bg-[#050608]" data-testid="auth-loading" />;
  if (bg && b.login_panel_in_image) return <InImageLayout bg={bg} pct={b.login_panel_pct || 36} title={title}>{children}</InImageLayout>;
  return (
    <div className="min-h-screen flex bg-[#050608] relative overflow-hidden" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="auth-layout">
      {bg && <img src={bg} alt="" data-testid="login-background-image" className="absolute inset-0 lg:left-[30%] h-full w-full lg:w-[70%] object-cover" />}
      <div className="relative z-10 w-full lg:w-[37%] min-h-screen auth-clip" style={{ background: GOLD }}>
        <div className="absolute inset-0 lg:right-[3px] flex items-center justify-center px-8 sm:px-14 auth-clip"
          style={{ background: "radial-gradient(ellipse at 30% 30%, #13213d 0%, #0b1428 55%, #070d1c 100%)" }}>
          <div className="w-full max-w-[340px] fade-up"><Title>{title}</Title><Divider />{children}</div>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full h-10 rounded-[2px] bg-white/95 px-3.5 text-[13px] text-slate-900 placeholder:text-slate-400 outline-none border border-transparent focus:border-[#C2A462] focus:ring-1 focus:ring-[#C2A462]/50 transition-colors";
export const GoldButton = ({ children, ...p }) => (
  <button {...p} className="w-full h-10 rounded-[2px] text-[12px] uppercase tracking-[0.22em] text-[#0b1428] font-medium transition-[opacity,transform] hover:opacity-90 active:scale-[0.99] disabled:opacity-60" style={{ background: `linear-gradient(180deg, #D4BA7A 0%, ${GOLD} 100%)` }}>{children}</button>
);
const FieldLabel = ({ children }) => <label className="block text-[11px] uppercase tracking-[0.18em] mb-2" style={{ color: GOLD }}>{children}</label>;
const ErrorText = ({ children }) => children ? <div className="text-xs text-red-400 mt-3" data-testid="auth-error">{children}</div> : null;
const linkCls = "text-[12px] tracking-wide text-[#7FB8E3] hover:text-[#a9d1f0] transition-colors";

function PasswordInput({ value, onChange, placeholder, testid }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input data-testid={testid} type={show ? "text" : "password"} className={`${inputCls} pr-12`} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} required />
      <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-3 text-slate-400 hover:text-slate-600" data-testid={`${testid}-toggle`}>{show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
    </div>
  );
}

function Credentials({ onNext }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const { data } = await api.post("/auth/login", { email, password }); onNext(data); }
    catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid="login-form">
      <FieldLabel>Email</FieldLabel>
      <input data-testid="login-email-input" type="email" className={inputCls} placeholder="Enter your email address" value={email} onChange={(e) => setEmail(e.target.value)} required />
      <div className="mt-5"><FieldLabel>Password</FieldLabel>
        <PasswordInput testid="login-password-input" value={password} onChange={setPassword} placeholder="Enter your password" /></div>
      <div className="text-right mt-2"><Link to="/forgot-password" className={linkCls} data-testid="forgot-password-link">Forgot password?</Link></div>
      <ErrorText>{err}</ErrorText>
      <div className="mt-7"><GoldButton type="submit" disabled={busy} data-testid="login-submit-button">{busy ? "Signing in…" : "Sign in"}</GoldButton></div>
      <div className="mt-3 flex items-center justify-center gap-1.5 text-[11px] tracking-wide text-[#C2A462]/80" data-testid="login-au-data"><ShieldCheck className="h-3.5 w-3.5" />Data stored securely</div>
    </form>
  );
}

function ChooseChannel({ mfa, onSent }) {
  const [channel, setChannel] = useState(mfa.phone_masked ? "sms" : "email");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true); setErr("");
    try { const { data } = await api.post("/auth/mfa/send", { mfa_token: mfa.mfa_token, channel }); onSent(data); }
    catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  const opt = (v, label, val, disabled) => (
    <label className={`flex items-start gap-3 py-2 ${disabled ? "opacity-40" : "cursor-pointer"}`} data-testid={`mfa-option-${v}`}>
      <input type="radio" name="ch" className="mt-0.5 h-4 w-4 accent-[#C2A462]" checked={channel === v} disabled={disabled} onChange={() => setChannel(v)} />
      <span className="grid grid-cols-[120px_1fr] gap-3 text-[13px] text-slate-400"><span>{label}</span><span className="break-all">{val || "No number on file"}</span></span>
    </label>
  );
  return (
    <div data-testid="mfa-choose">
      {opt("email", "Send code to email", mfa.email_masked)}
      {opt("sms", "Send code to phone", mfa.phone_masked, !mfa.phone_masked)}
      <ErrorText>{err}</ErrorText>
      <button onClick={send} disabled={busy} data-testid="send-security-code-btn" className="mt-6 w-full h-10 rounded-[2px] bg-[#3aa0dc] hover:bg-[#2f92cc] text-white text-[12px] uppercase tracking-[0.22em] font-medium transition-colors disabled:opacity-60">{busy ? "Sending…" : "Send Security Code"}</button>
    </div>
  );
}

function EnterCode({ mfa, sent, onDone, onResend }) {
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const verify = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const { data } = await api.post("/auth/mfa/verify", { mfa_token: mfa.mfa_token, code }); await onDone(data); }
    catch (x) { setErr(errMsg(x)); setBusy(false); }
  };
  return (
    <form onSubmit={verify} data-testid="mfa-verify-form">
      <p className="text-[13px] text-slate-400 mb-6 leading-relaxed">We sent a 6-digit code to <span className="text-slate-200">{sent.sent_to}</span>.</p>
      <FieldLabel>Security code</FieldLabel>
      <input data-testid="mfa-code-input" inputMode="numeric" maxLength={6} className={`${inputCls} tracking-[0.5em] text-center text-base`} placeholder="••••••" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} required />
      <ErrorText>{err}</ErrorText>
      <div className="mt-7"><GoldButton type="submit" disabled={busy || code.length !== 6} data-testid="verify-code-btn">{busy ? "Verifying…" : "Verify & Sign in"}</GoldButton></div>
      <button type="button" onClick={onResend} className={`mt-4 w-full ${linkCls}`} data-testid="resend-code-btn">Use a different method / resend code</button>
    </form>
  );
}

export function LoginPage() {
  const { user, completeLogin } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [mfa, setMfa] = useState(null);
  const [sent, setSent] = useState(null);
  if (user && !params.get("preview")) return <Navigate to="/" replace />;
  const done = async (data) => { const u = await completeLogin(data); const next = sessionStorage.getItem("bos_after_login"); sessionStorage.removeItem("bos_after_login"); nav(next && next.startsWith("/secure-doc/") ? next : u.role === "agency_owner" ? "/agency" : "/app/dashboard"); };
  if (!mfa) return <AuthLayout title="Sign into your account">{params.get("expired") && <p className="mb-4 rounded border border-[#C2A462]/30 px-3 py-2 text-[12px] text-[#EBD6A0]" data-testid="session-expired-msg">Your sign-in has expired for security. Please sign in again.</p>}<Credentials onNext={setMfa} /></AuthLayout>;
  if (!sent) return <AuthLayout title="Verify Security Code"><ChooseChannel mfa={mfa} onSent={setSent} /></AuthLayout>;
  return <AuthLayout title="Verify Security Code"><EnterCode mfa={mfa} sent={sent} onDone={done} onResend={() => setSent(null)} /></AuthLayout>;
}

export function ForgotPasswordPage() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await api.post("/auth/forgot-password", { email }); setSent(true); } catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <AuthLayout title="Reset Password">
      {sent ? (
        <div data-testid="reset-link-sent">
          <p className="text-[13px] text-slate-400 leading-relaxed">A password reset link has been sent to your email address.</p>
          <p className="text-[13px] text-slate-400 leading-relaxed mt-4">If you don't have access to that email box, please contact your agency.</p>
          <div className="mt-8"><GoldButton onClick={() => nav("/login")} data-testid="proceed-to-login-btn">Proceed to Login</GoldButton></div>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="forgot-password-form">
          <FieldLabel>Email</FieldLabel>
          <input data-testid="forgot-email-input" type="email" className={inputCls} placeholder="Your email address" value={email} onChange={(e) => setEmail(e.target.value)} required />
          <ErrorText>{err}</ErrorText>
          <div className="mt-8"><GoldButton type="submit" disabled={busy} data-testid="reset-password-submit">{busy ? "Sending…" : "Reset Password"}</GoldButton></div>
          <div className="text-center mt-5"><Link to="/login" className={linkCls} data-testid="back-to-login-link">Back to sign in</Link></div>
        </form>
      )}
    </AuthLayout>
  );
}

export function ResetPasswordPage() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const isInvite = sp.get("invite") === "1";
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (pw.length < 8) return setErr("Password must be at least 8 characters");
    if (pw !== confirm) return setErr("Passwords do not match");
    setBusy(true);
    try { await api.post("/auth/reset-password", { token: sp.get("token") || "", new_password: pw }); setDone(true); }
    catch (x) { setErr(errMsg(x)); } finally { setBusy(false); }
  };
  return (
    <AuthLayout title={done ? (isInvite ? "Account Ready" : "Password Updated") : (isInvite ? "Set Up Your Account" : "Set New Password")}>
      {done ? (
        <div data-testid="password-updated">
          <p className="text-[13px] text-slate-400 leading-relaxed">{isInvite ? "Your password is set. You can now sign in to BOS HQ." : "Your password has been changed. You can now sign in with your new password."}</p>
          <div className="mt-8"><GoldButton onClick={() => nav("/login")} data-testid="proceed-to-login-btn">Proceed to Login</GoldButton></div>
        </div>
      ) : (
        <form onSubmit={submit} data-testid="set-password-form">
          <FieldLabel>New Password</FieldLabel>
          <PasswordInput testid="new-password-input" value={pw} onChange={setPw} placeholder="New Password" />
          <div className="mt-5"><FieldLabel>Confirm New Password</FieldLabel>
            <PasswordInput testid="confirm-password-input" value={confirm} onChange={setConfirm} placeholder="Confirm New Password" /></div>
          <ErrorText>{err}</ErrorText>
          <div className="mt-8"><GoldButton type="submit" disabled={busy} data-testid="change-password-submit">{busy ? "Saving…" : isInvite ? "Set Password" : "Change Password"}</GoldButton></div>
        </form>
      )}
    </AuthLayout>
  );
}
